import { useEffect, useRef, useState } from 'react';
import FieldsEditor from './FieldsEditor';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { createDomain } from '@/services/domainRegistry';
import { buildDomainCapabilities } from '@/services/domain/centralModel';
import Modal from '@/components/ui/Modal/Modal';

const VIEW_OPTIONS = [
  { value: 'list', label: 'Class list', description: 'Record several learners in sequence' },
  { value: 'search', label: 'Search', description: 'Find and record one learner at a time' },
];

const slugify = (value) => value
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '');

function StepMarker({ number, label, active, complete }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
        active ? 'bg-primary text-primary-content' : complete ? 'bg-success/15 text-success' : 'bg-base-200 text-base-content/45'
      }`}>
        {number}
      </span>
      <span className={`truncate text-sm ${active ? 'font-semibold' : 'text-base-content/55'}`}>{label}</span>
    </div>
  );
}

export function DomainCreationForm({
  onSuccess,
  onCancel,
  schoolId,
  userId,
  parentDomainId,
  parentDomainLabel,
}) {
  const [step, setStep] = useState(1);
  const [addingField, setAddingField] = useState(false);
  const contentRef = useRef(null);
  useEffect(() => {
    const box = contentRef.current?.closest('.modal-box');
    if (box) box.scrollTop = 0;
  }, [step, addingField]);
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [scopeGrades, setScopeGrades] = useState('');
  const [eventTerm, setEventTerm] = useState('event');
  const [eventTermPlural, setEventTermPlural] = useState('events');
  const [actionTerm, setActionTerm] = useState('action');
  const [actionTermPlural, setActionTermPlural] = useState('actions');
  const [recordingForm, setRecordingForm] = useState({ primary: null, followUp: [] });
  const [recordingContext, setRecordingContext] = useState('event');
  const [assessmentForm, setAssessmentForm] = useState({ primary: { id: 'result', label: 'Result', type: 'number', optional: false }, followUp: [] });
  const [editorStarted, setEditorStarted] = useState(false);
  const [hasPendingField, setHasPendingField] = useState(false);
  const [defaultView, setDefaultView] = useState('list');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const assessmentRecording = recordingContext === 'assessment';
  const selectedForm = assessmentRecording ? assessmentForm : recordingForm;

  const continueToEvent = () => {
    if (!label.trim()) {
      setError('Give this Domain a name before continuing.');
      return;
    }
    setError(null);
    setEditorStarted(true);
    setStep(2);
    setAddingField(!assessmentRecording && !selectedForm.primary);
  };

  const handleSubmit = async () => {
    if (!label.trim() || !selectedForm.primary || hasPendingField) return;

    setError(null);
    setIsSubmitting(true);
    try {
      const domain = await createDomain(
        schoolId,
        {
          name: slugify(label) || `domain-${Date.now()}`,
          label: label.trim(),
          description: description.trim(),
          color: 'primary',
          defaultView,
          ...selectedForm,
          ...(parentDomainId ? { parentDomainId } : {}),
          scope: {
            grades: scopeGrades.split(',').map((grade) => grade.trim()).filter(Boolean),
          },
          capabilities: buildDomainCapabilities({
            recording: { enabled: true, ...(assessmentRecording ? { context: 'assessment' } : {}) },
          }),
          terminology: {
            event: { label: eventTerm.trim() || 'event', plural: eventTermPlural.trim() || 'events', description: '' },
            type: { label: 'type', plural: 'types', description: '' },
            action: { label: actionTerm.trim() || 'action', plural: actionTermPlural.trim() || 'actions', description: '' },
            category: { label: 'category', plural: 'categories', description: '' },
            rule: { label: 'rule', plural: 'rules', description: '' },
            queue: { label: 'queue', plural: 'queues', description: '' },
          },
        },
        userId || 'user',
      );
      onSuccess?.(domain);
    } catch (createError) {
      setError(createError.message || 'Failed to create this Domain. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen nativeDialog size="lg"
      title={addingField ? 'Add an event field' : parentDomainId ? 'New sub-domain' : 'New Domain'}
      onClose={addingField ? () => setAddingField(false) : onCancel}
      closeDisabled={isSubmitting}>
    <div ref={contentRef} className="space-y-6">
      {!addingField && <div className="flex items-start justify-between gap-4">
        <div>
          <p className="mt-1 text-sm text-base-content/60">
            {step === 1 ? 'Name the area and choose who sees it.' : 'Choose the questions staff complete when recording.'}
          </p>
          {parentDomainId && (
            <p className="mt-2 text-xs font-medium text-base-content/55">
              Sub-Domain of {parentDomainLabel || 'the selected Domain'}
            </p>
          )}
        </div>
        <span className="shrink-0 whitespace-nowrap text-xs text-base-content/45">{step} of 2</span>
      </div>}

      {!addingField && <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3" aria-label="Creation progress">
        <StepMarker number="1" label="Domain" active={step === 1} complete={step > 1} />
        <span className="h-px w-8 bg-base-300 sm:w-16" />
        <StepMarker number="2" label="Recording form" active={step === 2} />
      </div>}

      {!addingField && error && <div className="alert alert-error py-3 text-sm" role="alert"><span>{error}</span></div>}

      <form id="new-domain-identity" hidden={step !== 1 || addingField} className="space-y-5" onSubmit={(event) => { event.preventDefault(); continueToEvent(); }}>
          <div className="flex flex-col gap-2">
            <label htmlFor="new-domain-name" className="text-sm font-medium">Name</label>
            <input
              id="new-domain-name"
              type="text"
              className="input input-bordered w-full"
              placeholder="e.g. Consent Forms"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              autoFocus
              required
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="new-domain-description" className="text-sm font-medium">Description</label>
            <textarea
              id="new-domain-description"
              className="textarea textarea-bordered min-h-20 w-full resize-none"
              placeholder="What will this help your school keep track of?"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="new-domain-grades" className="text-sm font-medium">Grades</label>
            <input
              id="new-domain-grades"
              type="text"
              className="input input-bordered w-full"
              placeholder="All grades, or e.g. 7, 8, 9"
              value={scopeGrades}
              onChange={(event) => setScopeGrades(event.target.value)}
            />
            <p className="text-xs text-base-content/50">Leave empty for all grades.</p>
          </div>

          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold">Words staff will see</legend>
            <p className="text-xs text-base-content/50">Name a recorded item and the follow-up it can create.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="new-domain-event-term" className="text-xs font-medium">Recorded item — singular</label>
                <input id="new-domain-event-term" className="input input-bordered input-sm w-full" value={eventTerm} onChange={(event) => setEventTerm(event.target.value)} placeholder="event" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="new-domain-event-plural" className="text-xs font-medium">Recorded item — plural</label>
                <input id="new-domain-event-plural" className="input input-bordered input-sm w-full" value={eventTermPlural} onChange={(event) => setEventTermPlural(event.target.value)} placeholder="events" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="new-domain-action-term" className="text-xs font-medium">Follow-up — singular</label>
                <input id="new-domain-action-term" className="input input-bordered input-sm w-full" value={actionTerm} onChange={(event) => setActionTerm(event.target.value)} placeholder="action" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="new-domain-action-plural" className="text-xs font-medium">Follow-up — plural</label>
                <input id="new-domain-action-plural" className="input input-bordered input-sm w-full" value={actionTermPlural} onChange={(event) => setActionTermPlural(event.target.value)} placeholder="actions" />
              </div>
            </div>
          </fieldset>

          <div className="flex flex-col gap-2">
            <label htmlFor="new-domain-recording-context" className="text-sm font-medium">Recording workflow</label>
            <select id="new-domain-recording-context" className="select select-bordered w-full" value={recordingContext} onChange={event => setRecordingContext(event.target.value)}>
              <option value="event">Individual records</option>
              <option value="assessment">Programme assessments</option>
            </select>
            <p className="text-xs text-base-content/55">{assessmentRecording
              ? 'Use the school’s Academic Programmes for subjects, assessments, Points or Levels, totals and optional HOD review. Add other questions to the recording form.'
              : 'Staff record an event whenever needed, using the questions you add to the form.'}</p>
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Where should staff start?</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {VIEW_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
                    defaultView === option.value ? 'border-primary bg-primary/5' : 'border-base-300 hover:border-primary/40'
                  }`}
                >
                  <input
                    type="radio"
                    className="radio radio-primary radio-sm mt-0.5"
                    name="newDomainDefaultView"
                    checked={defaultView === option.value}
                    onChange={() => setDefaultView(option.value)}
                  />
                  <span>
                    <span className="block text-sm font-medium">{option.label}</span>
                    <span className="mt-0.5 block text-xs text-base-content/55">{option.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
      </form>
      <div hidden={step !== 2}>
        {assessmentRecording && !addingField && <div className="mb-4 rounded-lg border border-base-300 p-3">
          <h3 className="text-sm font-semibold">Assessment result</h3>
          <p className="mt-1 text-sm text-base-content/60">The selected Programme assessment supplies the result input and status alternatives. Add any other questions below; staff complete them in each learner’s details.</p>
        </div>}
        {editorStarted && <FieldsEditor
          domain={{ id: 'new-domain-draft', ...selectedForm }}
          onChange={assessmentRecording ? setAssessmentForm : setRecordingForm}
          onPendingChange={setHasPendingField}
          supplementaryOnly={assessmentRecording}
          addFieldOpen={addingField}
          onAddFieldOpenChange={setAddingField}
          replaceContentWhenAdding
        />}
        {!addingField && <p className="mt-3 text-xs text-base-content/55">Combine the inputs this Domain needs. You can continue configuring its form and follow-up behaviour after creation.</p>}
      </div>

      {!addingField && <div className="flex flex-col-reverse gap-2 border-t border-base-200 pt-4 sm:flex-row sm:justify-end">
        {step === 1 ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        ) : (
          <button type="button" className="btn btn-ghost btn-sm gap-2" onClick={() => { setError(null); setStep(1); }} disabled={isSubmitting}>
            <ArrowLeftIcon className="h-4 w-4" /> Back
          </button>
        )}
        {step === 1 ? (
          <button type="submit" form="new-domain-identity" className="btn btn-primary btn-sm gap-2" disabled={!label.trim()}>
            Continue <ArrowRightIcon className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedForm.primary || hasPendingField}
            aria-description={hasPendingField ? 'Add or cancel the field being edited before creating this Domain' : !selectedForm.primary ? 'Add the first field to create this Domain' : undefined}
          >
            {isSubmitting && <span className="loading loading-spinner loading-xs" />}
            Create Domain
          </button>
        )}
      </div>}
    </div>
    </Modal>
  );
}

export default DomainCreationForm;
